import React, { useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { FiX } from "react-icons/fi";
import { formatarMoeda } from "../../../utils/calculos";
import { ETAPA_LABELS, ORDEM_ETAPAS } from "../../../utils/toc/etapas";
import {
  DrawerBody,
  DrawerCloseBtn,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPanel,
  DrawerSection,
  DrawerSectionTitle,
  FieldRow,
  GhostButton,
  HelperText,
  NumberInput,
  PrimaryButton,
  Select,
  Slider,
  SliderRow,
  TotalLine,
  DrawerTitle,
} from "./styles";

const OE_LABELS = {
  folha: "Folha",
  encargos: "Encargos",
  aluguel: "Aluguel",
  energia: "Energia",
  depreciacao: "Depreciação",
  administrativas: "Despesas administrativas",
  comerciais: "Despesas comerciais fixas",
  outras: "Outras",
};

function parseEntrada(value) {
  if (value === "" || value == null) return 0;
  const numero = Number(String(value).replace(",", "."));
  return Number.isFinite(numero) ? numero : 0;
}

export default function ParametrosDrawer({
  open,
  onClose,
  parametros,
  atualizar,
  atualizarOE,
  atualizarZonas,
  definirCcr,
  resetar,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const totalOE = Object.values(parametros.oeMensal).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <DrawerOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />
          <DrawerPanel
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22 }}
            role="dialog"
            aria-label="Parâmetros da TOC"
          >
            <DrawerHeader>
              <DrawerTitle>Parâmetros TOC</DrawerTitle>
              <DrawerCloseBtn type="button" onClick={onClose}>
                <FiX size={14} />
                Fechar
              </DrawerCloseBtn>
            </DrawerHeader>
            <DrawerBody>
              <DrawerSection>
                <DrawerSectionTitle>Materiais e metas do mês</DrawerSectionTitle>
                <FieldRow>
                  <span>% materiais médio (T = receita × (1 − %))</span>
                  <NumberInput
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={(parametros.pctMateriaisMedio * 100).toFixed(1)}
                    onChange={(event) =>
                      atualizar({
                        pctMateriaisMedio:
                          parseEntrada(event.target.value) / 100,
                      })
                    }
                  />
                </FieldRow>
                <FieldRow>
                  <span>Meta de ganho mensal (R$)</span>
                  <NumberInput
                    type="number"
                    min="0"
                    step="1000"
                    value={parametros.metaGanhoMesRS}
                    onChange={(event) =>
                      atualizar({ metaGanhoMesRS: parseEntrada(event.target.value) })
                    }
                  />
                </FieldRow>
                <FieldRow>
                  <span>Meta de ambientes/mês</span>
                  <NumberInput
                    type="number"
                    min="0"
                    step="1"
                    value={parametros.metaAmbientesMes}
                    onChange={(event) =>
                      atualizar({ metaAmbientesMes: parseEntrada(event.target.value) })
                    }
                  />
                </FieldRow>
              </DrawerSection>

              <DrawerSection>
                <DrawerSectionTitle>OE — Despesa Operacional do mês (R$)</DrawerSectionTitle>
                {Object.entries(OE_LABELS).map(([chave, label]) => (
                  <FieldRow key={chave}>
                    <span>{label}</span>
                    <NumberInput
                      type="number"
                      min="0"
                      step="100"
                      value={parametros.oeMensal[chave] || 0}
                      onChange={(event) =>
                        atualizarOE(chave, parseEntrada(event.target.value))
                      }
                    />
                  </FieldRow>
                ))}
                <TotalLine>
                  <span>Total OE</span>
                  <span>{formatarMoeda(totalOE)}</span>
                </TotalLine>
              </DrawerSection>

              <DrawerSection>
                <DrawerSectionTitle>Zonas de pulmão (% consumido)</DrawerSectionTitle>
                <SliderRow>
                  <span>Verde até</span>
                  <Slider
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(parametros.zonasPulmao.verde * 100)}
                    onChange={(event) =>
                      atualizarZonas({
                        verde: parseEntrada(event.target.value) / 100,
                      })
                    }
                  />
                  <span>{Math.round(parametros.zonasPulmao.verde * 100)}%</span>
                </SliderRow>
                <SliderRow>
                  <span>Amarelo até</span>
                  <Slider
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(parametros.zonasPulmao.amarelo * 100)}
                    onChange={(event) =>
                      atualizarZonas({
                        amarelo: parseEntrada(event.target.value) / 100,
                      })
                    }
                  />
                  <span>{Math.round(parametros.zonasPulmao.amarelo * 100)}%</span>
                </SliderRow>
                <SliderRow>
                  <span>Vermelho até</span>
                  <Slider
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(parametros.zonasPulmao.vermelho * 100)}
                    onChange={(event) =>
                      atualizarZonas({
                        vermelho: parseEntrada(event.target.value) / 100,
                      })
                    }
                  />
                  <span>{Math.round(parametros.zonasPulmao.vermelho * 100)}%</span>
                </SliderRow>
                <HelperText>
                  Limites são forçados a ficar em ordem crescente (verde ≤ amarelo ≤
                  vermelho) automaticamente.
                </HelperText>
              </DrawerSection>

              <DrawerSection>
                <DrawerSectionTitle>CCR (recurso restritivo)</DrawerSectionTitle>
                <FieldRow>
                  <span>Forçar etapa do CCR</span>
                  <Select
                    value={parametros.ccrEtapa || ""}
                    onChange={(event) =>
                      definirCcr(event.target.value || null)
                    }
                  >
                    <option value="">Auto (gargalo do dia)</option>
                    {ORDEM_ETAPAS.map((etapa) => (
                      <option key={etapa} value={etapa}>
                        {ETAPA_LABELS[etapa]}
                      </option>
                    ))}
                  </Select>
                </FieldRow>
                <HelperText>
                  "Auto" usa a etapa com maior carga. Forçar é útil quando você
                  sabe que o CCR estratégico é outro. A capacidade da fábrica é
                  medida pelo ritmo real em Legado &gt; Capacidade da Fábrica.
                </HelperText>
              </DrawerSection>
            </DrawerBody>
            <DrawerFooter>
              <GhostButton type="button" onClick={resetar}>
                Restaurar padrões
              </GhostButton>
              <PrimaryButton type="button" onClick={onClose}>
                Aplicar e fechar
              </PrimaryButton>
            </DrawerFooter>
          </DrawerPanel>
        </>
      )}
    </AnimatePresence>
  );
}
